import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import { FeedbackResponseType, WorkerCommentEntry } from '@scale/shared/types/chat/feedback';
import { InteractionStepType, StepParamsForTypeWithStage } from '@scale/shared/types/chat';

export const constructStepChildId = <U extends InteractionStepType>(
  step: StepParamsForTypeWithStage<U>,
  additionalId?: string,
): string => {
  return additionalId
    ? `${step.type}-${step.responseIndex}-${additionalId}`
    : `${step.type}-${step.responseIndex}`;
};

export type BatchUpdateCommentLocation = {
  id: string;
  updater: (
    locations: [number | undefined, number | undefined],
  ) => [number | undefined, number | undefined];
};

export type Selection = {
  from: number;
  to: number;
  empty: boolean;
  childId: string;
  text?: string;
};

export type WorkerCommentsStore = {
  workerComments: WorkerCommentEntry[];
  selectedWorkerComment: string | null;

  selection: Selection;

  addWorkerComment: (workerComment: WorkerCommentEntry) => void;
  setWorkerComments: (workerComments: WorkerCommentEntry[]) => void;

  setSelection: (selection: Selection) => void;
  deleteSelection: () => void;

  // We can only fetch worker comments based on their childId and location
  getWorkerCommentByChildId(childId: string): WorkerCommentEntry | undefined;
  getWorkerCommentById(workerCommentId: string): WorkerCommentEntry | undefined;
  getAllWorkerCommentsByChildId(childId: string): WorkerCommentEntry[];

  // Once created, comments should have an ID either from Mongo or a local UUID
  selectWorkerComment: (workerCommentId: string | null) => void;
  updateWorkerComment: (workerCommentId: string, comment: string, title?: string) => void;
  updateWorkerCommentLocation: (
    workerCommentId: BatchUpdateCommentLocation['id'],
    updater: BatchUpdateCommentLocation['updater'],
  ) => void;
  batchUpdateWorkerCommentLocations: (locations: Array<BatchUpdateCommentLocation>) => void;
  deleteWorkerComment: (workerCommentId: string) => void;
  addFeedbackResponseToWorkerComment: (
    workerCommentId: string,
    feedbackResponse: FeedbackResponseType,
  ) => void;
  resetWorkerCommentsFromResponseIndex: (
    stepParams: StepParamsForTypeWithStage<InteractionStepType>[],
    responseIndex: number,
  ) => void;
};

export const useWorkerCommentsStore = create<WorkerCommentsStore>()(
  devtools(
    subscribeWithSelector((set, get) => ({
      workerComments: [],
      selectedWorkerComment: null,
      selection: {
        from: 0,
        to: 0,
        empty: true,
        childId: '',
      },

      addWorkerComment: (workerComment: WorkerCommentEntry) => {
        if (workerComment.comment.length) {
          set(state => ({
            workerComments: [...state.workerComments, workerComment],
          }));
        }
      },
      setWorkerComments: (workerComments: WorkerCommentEntry[]) => {
        set({ workerComments });
      },

      setSelection: (selection: Selection) => {
        set(_ => ({ selection }));
      },
      deleteSelection: () => {
        set({
          selection: {
            from: 0,
            to: 0,
            empty: true,
            childId: '',
          },
        });
      },

      getAllWorkerCommentsByChildId: (childId: string) => {
        return get().workerComments.filter(workerComment => workerComment.childId === childId);
      },
      selectWorkerComment: (workerCommentId: string | null) => {
        set(_ => ({
          selectedWorkerComment: workerCommentId,
        }));
      },

      getWorkerCommentByChildId: (childId: string) => {
        return get().workerComments.find(workerComment => workerComment.childId === childId);
      },
      getWorkerCommentById: (workerCommentId: string) => {
        return get().workerComments.find(
          workerComment => workerComment.workerCommentId === workerCommentId,
        );
      },

      updateWorkerComment: (workerCommentId: string, comment: string, title?: string) => {
        set(state => ({
          workerComments: state.workerComments.map(workerComment => {
            if (workerComment.workerCommentId === workerCommentId) {
              return {
                ...workerComment,
                comment,
                title: title ?? workerComment.title,
              };
            } else {
              return workerComment;
            }
          }),
        }));
      },

      updateWorkerCommentLocation(
        workerCommentId: BatchUpdateCommentLocation['id'],
        updater: BatchUpdateCommentLocation['updater'],
      ) {
        set(state => ({
          workerComments: state.workerComments.map(workerComment => {
            if (workerComment.workerCommentId === workerCommentId) {
              const [locationStart, locationEnd] = updater([
                workerComment.locationStart,
                workerComment.locationEnd,
              ]);

              return {
                ...workerComment,
                locationStart,
                locationEnd,
              };
            } else {
              return workerComment;
            }
          }),
        }));
      },
      batchUpdateWorkerCommentLocations(locations: Array<BatchUpdateCommentLocation>) {
        set(state => {
          const commentIds = locations.map(location => location.id);
          return {
            workerComments: state.workerComments.map(workerComment => {
              if (commentIds.includes(workerComment.workerCommentId)) {
                const locationUpdaterObject = locations.find(
                  location => location.id === workerComment.workerCommentId,
                );
                const [locationStart, locationEnd] = locationUpdaterObject!.updater([
                  workerComment.locationStart,
                  workerComment.locationEnd,
                ]);

                return {
                  ...workerComment,
                  locationStart,
                  locationEnd,
                };
              } else {
                return workerComment;
              }
            }),
          };
        });
      },
      deleteWorkerComment: (workerCommentId: string) => {
        set(state => ({
          workerComments: state.workerComments.filter(
            workerComment => workerComment.workerCommentId !== workerCommentId,
          ),
        }));
      },
      addFeedbackResponseToWorkerComment: (
        workerCommentId: string,
        feedbackResponse: FeedbackResponseType,
      ) => {
        set(state => ({
          workerComments: state.workerComments.map(workerComment => {
            if (workerComment.workerCommentId === workerCommentId) {
              return {
                ...workerComment,
                feedbackResponse,
              };
            } else {
              return workerComment;
            }
          }),
        }));
      },
      resetWorkerCommentsFromResponseIndex: (
        stepParams: StepParamsForTypeWithStage<InteractionStepType>[],
        responseIndex: number,
      ) => {
        set(state => {
          // Get all steps after the specified response index
          const stepsToRemoveFeedbackFor = stepParams.slice(responseIndex + 1);

          // Get childIds from steps to remove feedback for
          const childIdsToTarget = stepsToRemoveFeedbackFor.map(step => constructStepChildId(step));

          // Filter out worker comments for the steps to remove feedback for
          const filteredWorkerComments = state.workerComments.filter(
            workerComment =>
              !workerComment.childId || !childIdsToTarget.includes(workerComment.childId),
          );
          return { workerComments: filteredWorkerComments };
        });
      },
    })),
  ),
);
